import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ResponsiveBar } from "@nivo/bar";
import { ResponsiveLine } from "@nivo/line";
import { ResponsivePie } from "@nivo/pie";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Divider,
  FormControlLabel,
  Grid,
  MenuItem,
  Modal,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CountUp from "react-countup";
import axios from "axios";
import AssignmentTurnedInOutlinedIcon from "@mui/icons-material/AssignmentTurnedInOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import CloudDownloadOutlinedIcon from "@mui/icons-material/CloudDownloadOutlined";
import DoneAllOutlinedIcon from "@mui/icons-material/DoneAllOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import LocalHospitalOutlinedIcon from "@mui/icons-material/LocalHospitalOutlined";
import PendingActionsOutlinedIcon from "@mui/icons-material/PendingActionsOutlined";
import PersonAddAltOutlinedIcon from "@mui/icons-material/PersonAddAltOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import PublishedWithChangesOutlinedIcon from "@mui/icons-material/PublishedWithChangesOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { useNavigate } from "react-router-dom";
import AgregarParticipantesConvocatoriaModal from "components/AgregarParticipantesConvocatoriaModal";
import EstadoOperativoDialog from "components/EstadoOperativoDialog";
import HistorialEstadoOperativoDialog from "components/HistorialEstadoOperativoDialog";
import VoluntariosConvocatoriaOperativaDialog, {
  formatActivity,
} from "components/VoluntariosConvocatoriaOperativaDialog";
import { MEDICAL_DEPLOYMENT_LABELS } from "constants/dashboardTerminology";
import {
  canonicalOperationalStateOptions,
  getEstadoOperativoVisual,
  getOperationalOriginLabel,
  isTerminalOperationalStatus,
  normalizeOperationalStatus,
} from "utils/operationalStatus";
import JornadasMap from "../dashboardSaludEmergencia/JornadasMap";

const DashboardCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  [theme.breakpoints.up("sm")]: {
    padding: theme.spacing(2.5),
  },
  borderRadius: "20px",
  backgroundColor: theme.palette.background.alt,
  border: "1px solid rgba(15, 23, 42, 0.08)",
  boxShadow: "0 12px 28px rgba(15, 23, 42, 0.08)",
  height: "100%",
}));

const FilterCard = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.mode === "dark" ? theme.palette.primary[600] : "#f8fbfb",
  border: "1px solid rgba(15, 23, 42, 0.08)",
  borderRadius: "16px",
  padding: theme.spacing(1.5),
  height: "100%",
}));

const initialFilters = {
  idProyecto: "",
  codigoProyecto: "",
  idConvocatoria: "",
  fechaDesde: "",
  fechaHasta: "",
  estadoOperativo: "",
  estadoConvocatoria: "",
  comunidad: "",
  actividad: "",
  todosProyectos: false,
};

const DASHBOARD_STATE_KEY = "dashboardOperativo:navigationState";
const MAX_STORED_STATE_AGE = 12 * 60 * 60 * 1000;
const dashboardFilterKeys = Object.keys(initialFilters);

const normalizeDashboardFilters = (value) => {
  const source = value && typeof value === "object" ? value : {};

  return dashboardFilterKeys.reduce(
    (normalized, key) => ({
      ...normalized,
      [key]: key === "todosProyectos" ? source[key] === true : source[key] ?? initialFilters[key],
    }),
    {}
  );
};

const getStoredDashboardState = () => {
  try {
    const raw = sessionStorage.getItem(DASHBOARD_STATE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    const savedAt = Number(parsed?.savedAt);

    if (!parsed || typeof parsed !== "object" || !savedAt || Date.now() - savedAt > MAX_STORED_STATE_AGE) {
      sessionStorage.removeItem(DASHBOARD_STATE_KEY);
      return null;
    }

    const parsedRowsPerPage = Number(parsed.rowsPerPage);

    return {
      filters: normalizeDashboardFilters(parsed.filters),
      appliedFilters: normalizeDashboardFilters(parsed.appliedFilters || parsed.filters),
      page: Math.max(0, Number(parsed.page) || 0),
      rowsPerPage: [25, 50, 100].includes(parsedRowsPerPage) ? parsedRowsPerPage : 25,
      scrollY: Math.max(0, Number(parsed.scrollY) || 0),
      savedAt,
    };
  } catch (error) {
    return null;
  }
};

const saveDashboardState = ({ filters, appliedFilters, page, rowsPerPage, scrollY }) => {
  try {
    sessionStorage.setItem(
      DASHBOARD_STATE_KEY,
      JSON.stringify({
        filters: normalizeDashboardFilters(filters),
        appliedFilters: normalizeDashboardFilters(appliedFilters || filters),
        page: Math.max(0, Number(page) || 0),
        rowsPerPage: [25, 50, 100].includes(Number(rowsPerPage)) ? Number(rowsPerPage) : 25,
        scrollY: Math.max(0, Number(scrollY) || 0),
        savedAt: Date.now(),
      })
    );
  } catch (error) {
    // Navigation must continue when sessionStorage is unavailable.
  }
};

const chartColors = ["#009fe3", "#008080", "#f39313", "#22a06b", "#d94841", "#004f7c"];

const allowedRoles = ["1", "2", "6", "8"];

const safeJsonParse = (value, fallback = {}) => {
  try {
    return value ? JSON.parse(value) : fallback;
  } catch (error) {
    return fallback;
  }
};

const formatDate = (date) => {
  if (!date || typeof date !== "string" || !date.includes("-")) return date || "-";
  const [year, month, day] = date.split("-");
  return `${day}/${month}/${year}`;
};

const formatDateTime = (date) =>
  new Intl.DateTimeFormat("es-VE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);

const sortDateAsc = (a, b) => String(a).localeCompare(String(b));

const asNumber = (value) => Number(value || 0);

const textOrDash = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  return value;
};

const formatBackendDateTime = (value) => {
  if (!value) return "-";
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2}))?/);
  if (!match) return String(value);

  const date = `${match[3]}/${match[2]}/${match[1]}`;
  return match[4] ? `${date} ${match[4]}:${match[5]}` : date;
};

const isTruthyFlag = (value) => value === true || value === 1 || value === "1" || value === "true";

const canAddParticipants = (row = {}) => {
  const generalState =
    row.estadoConvocatoria ??
    row.estado_convocatoria ??
    row.estadoConvo ??
    row.estadoGeneral ??
    row.estado_general;

  if (/^\d+$/.test(String(generalState ?? "").trim())) {
    return String(generalState) === "1";
  }

  return ["en_espera", "en_preparacion", "desplegado"].includes(
    normalizeOperationalStatus(row.estadoOperativo ?? row.estado_operativo)
  );
};

const toConvocatoriaContext = (row = {}) => {
  const idConvocatoria = row.idConvocatoria ?? row.id_convocatoria ?? row.id;
  const idProyecto = row.idProyecto ?? row.id_proyecto ?? row.idproyectos;

  return {
    ...row,
    id: idConvocatoria,
    idConvocatoria,
    idProyecto,
  };
};

const getProjectId = (option) =>
  option?.idProyecto ?? option?.id_proyecto ?? option?.id ?? option?.value ?? option?.codigo_proyecto ?? "";

const getProjectCode = (option) => option?.codigoProyecto ?? option?.codigo_proyecto ?? option?.codigo ?? "";

const getProjectName = (option) =>
  option?.nombreProyecto ?? option?.nombre_proyecto ?? option?.nombre ?? option?.label ?? getProjectCode(option);

const getProjectVisual = (option) =>
  option?.codigoProyectoVisual ?? option?.codigo_proyecto_visual ?? option?.codigoVisual ?? getProjectCode(option);

const getConvocatoriaId = (option) =>
  option?.idConvocatoria ?? option?.id_convocatoria ?? option?.id ?? option?.value ?? "";

const getConvocatoriaLabel = (option) =>
  formatActivity(
    option?.descripcionConvo ??
      option?.descripcion_convo ??
      option?.nombreJornada ??
      option?.nombre_jornada ??
      option?.actividad ??
      option?.label ??
      getConvocatoriaId(option)
  );

const getOptionValue = (option, keys = []) => {
  if (typeof option === "string") return option;

  for (const key of keys) {
    if (option?.[key] !== undefined && option?.[key] !== null && option?.[key] !== "") return option[key];
  }

  return option?.value ?? option?.id ?? option?.label ?? option?.nombre ?? "";
};

const getOptionLabel = (option, keys = []) => {
  if (typeof option === "string") return option;

  for (const key of keys) {
    if (option?.[key] !== undefined && option?.[key] !== null && option?.[key] !== "") return option[key];
  }

  return option?.label ?? option?.nombre ?? option?.value ?? option?.id ?? "";
};

const buildParams = (filters, rol) => {
  const params = {};

  if (filters.idProyecto) params.id_proyecto = filters.idProyecto;
  if (filters.codigoProyecto) params.codigo_proyecto = filters.codigoProyecto;
  if (filters.idConvocatoria) params.id_convocatoria = filters.idConvocatoria;
  if (filters.fechaDesde) params.fecha_desde = filters.fechaDesde;
  if (filters.fechaHasta) params.fecha_hasta = filters.fechaHasta;
  if (filters.estadoConvocatoria) params.estado_convocatoria = filters.estadoConvocatoria;
  if (filters.estadoOperativo) params.estado_operativo = filters.estadoOperativo;
  if (filters.comunidad) params.comunidad = filters.comunidad;
  if (filters.actividad) params.tipo_actividad = filters.actividad;
  if (filters.todosProyectos && ["1", "2"].includes(String(rol))) params.todos_proyectos = true;

  return params;
};

const fetchDashboardData = async (filters, rol) => {
  const response = await axios.get("/dashboard-operativo", {
    params: buildParams(filters, rol),
  });
  const payload = response?.data?.data || response?.data || {};

  if (!payload || payload.ok === false) {
    throw new Error("Respuesta invalida del dashboard operativo");
  }

  return {
    meta: payload.meta || {},
    kpis: payload.kpis || {},
    charts: payload.charts || {},
    filters: payload.filters || {},
    tabla: Array.isArray(payload.tabla) ? payload.tabla : [],
    mapa: payload.mapa || {},
  };
};

const hasArrayData = (items) => Array.isArray(items) && items.length > 0;

const hasLineData = (lineData) =>
  Array.isArray(lineData) &&
  lineData.length > 0 &&
  Array.isArray(lineData[0].data) &&
  lineData[0].data.length > 0 &&
  lineData[0].data.every((point) => point.x && point.y !== null && point.y !== undefined);

const hasBarData = (items, keys = ["total"]) =>
  hasArrayData(items) &&
  items.some((item) => keys.some((key) => Number(item[key]) > 0)) &&
  items.every((item) => keys.every((key) => item[key] !== null && item[key] !== undefined));

const hasPieData = (items) =>
  hasArrayData(items) &&
  items.some((item) => Number(item.value) > 0) &&
  items.every((item) => item.id && item.value !== null && item.value !== undefined);

const getValueFromChartItem = (item) =>
  Number(item.total || item.cantidad || item.value || item.atenciones || item.jornadas || item.count || 0);

const limitChartRanking = (items = [], limit = 10, withOthers = true) => {
  if (!Array.isArray(items)) return [];

  const sortedItems = [...items].sort((a, b) => getValueFromChartItem(b) - getValueFromChartItem(a));

  if (sortedItems.length <= limit) return sortedItems;

  const topItems = sortedItems.slice(0, limit);
  const otherTotal = sortedItems.slice(limit).reduce((sum, item) => sum + getValueFromChartItem(item), 0);

  if (!withOthers || otherTotal <= 0) return topItems;

  return [
    ...topItems,
    {
      id: "Otros",
      label: "Otros",
      jornada: "Otros",
      comunidad: "Otros",
      total: otherTotal,
      value: otherTotal,
    },
  ];
};

const EmptyChartState = () => (
  <Box height="100%" display="flex" alignItems="center" justifyContent="center" textAlign="center" px={2}>
    <Typography variant="body2" color="text.secondary">
      No hay datos suficientes para este grafico.
    </Typography>
  </Box>
);

const SectionTitle = ({ title, pill, icon }) => (
  <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} mb={2}>
    <Typography variant="h5" sx={{ fontWeight: "bold", display: "flex", alignItems: "center" }}>
      {icon}
      {title}
    </Typography>
    {pill && (
      <Chip
        label={pill}
        size="small"
        sx={{ bgcolor: "rgba(0, 159, 227, 0.12)", color: "#004f7c", fontWeight: 800 }}
      />
    )}
  </Box>
);

const StatCard = ({ title, value, icon, caption, color = "#008080" }) => {
  const theme = useTheme();

  return (
    <DashboardCard>
      <Box display="flex" alignItems="center" gap={2}>
        <Avatar
          sx={{
            width: 56,
            height: 56,
            borderRadius: "18px",
            bgcolor: color,
            flexShrink: 0,
          }}
        >
          {icon}
        </Avatar>
        <Box minWidth={0}>
          <Typography variant="h3" sx={{ color: theme.palette.secondary[100], fontWeight: 900 }}>
            <CountUp end={Number(value) || 0} separator="." />
          </Typography>
          <Typography variant="body2" sx={{ color: theme.palette.secondary[200], fontWeight: 800 }}>
            {title}
          </Typography>
          <Typography variant="caption" sx={{ color: "#0f766e", fontWeight: 800 }}>
            {caption}
          </Typography>
        </Box>
      </Box>
    </DashboardCard>
  );
};

const DetailRow = ({ label, value }) => (
  <Box>
    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 800 }}>
      {textOrDash(value)}
    </Typography>
  </Box>
);

const DashboardOperativo = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const isMediumScreen = useMediaQuery(theme.breakpoints.down("md"));
  const tokenData = useMemo(() => safeJsonParse(localStorage.getItem("token"), {}), []);
  const storedDashboardState = useMemo(() => getStoredDashboardState(), []);
  const restoredScrollRef = useRef(false);
  const projectRecoveryMessageRef = useRef("");
  const rolActual = String(tokenData?.rol || "");
  const puedeVerDashboard = allowedRoles.includes(rolActual);
  const canUseTodosProyectos = ["1", "2"].includes(rolActual);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState(() => storedDashboardState?.filters || { ...initialFilters });
  const [appliedFilters, setAppliedFilters] = useState(
    () => storedDashboardState?.appliedFilters || storedDashboardState?.filters || { ...initialFilters }
  );
  const [lastUpdated, setLastUpdated] = useState(null);
  const [page, setPage] = useState(() => storedDashboardState?.page || 0);
  const [rowsPerPage, setRowsPerPage] = useState(() => storedDashboardState?.rowsPerPage || 25);
  const rowsPerPageRef = useRef(rowsPerPage);
  rowsPerPageRef.current = rowsPerPage;
  const [selectedRow, setSelectedRow] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [participantModalOpen, setParticipantModalOpen] = useState(false);
  const [participantConvocatoria, setParticipantConvocatoria] = useState(null);
  const [volunteersDialogOpen, setVolunteersDialogOpen] = useState(false);
  const [volunteersConvocatoria, setVolunteersConvocatoria] = useState(null);
  const [stateDialogOpen, setStateDialogOpen] = useState(false);
  const [stateConvocatoria, setStateConvocatoria] = useState(null);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [historyConvocatoria, setHistoryConvocatoria] = useState(null);
  const [dashboardResponse, setDashboardResponse] = useState({
    kpis: {},
    charts: {},
    filters: {},
    meta: {},
  });
  const [tableRows, setTableRows] = useState([]);
  const [mapPoints, setMapPoints] = useState([]);

  const loadData = useCallback(async () => {
    if (!puedeVerDashboard) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(projectRecoveryMessageRef.current);
      projectRecoveryMessageRef.current = "";
      const response = await fetchDashboardData(appliedFilters, rolActual);

      setDashboardResponse({
        kpis: response.kpis,
        charts: response.charts,
        filters: response.filters,
        meta: response.meta,
      });
      setTableRows(Array.isArray(response.tabla) ? response.tabla : []);
      setMapPoints(Array.isArray(response.mapa?.jornadas) ? response.mapa.jornadas : []);
      setLastUpdated(new Date());
    } catch (err) {
      setError("No se pudo actualizar el dashboard operativo.");
      setTableRows([]);
      setMapPoints([]);
      setDashboardResponse((current) => ({
        ...current,
        kpis: {},
        charts: {},
        meta: {},
      }));

      if (
        err?.response?.status === 403 &&
        (appliedFilters.idProyecto || appliedFilters.codigoProyecto)
      ) {
        projectRecoveryMessageRef.current =
          "El proyecto guardado ya no está disponible. Seleccione otro proyecto para continuar.";
        const clearedAppliedFilters = {
          ...appliedFilters,
          idProyecto: "",
          codigoProyecto: "",
          idConvocatoria: "",
          todosProyectos: false,
        };

        setFilters((current) => ({
          ...current,
          idProyecto: "",
          codigoProyecto: "",
          idConvocatoria: "",
          todosProyectos: false,
        }));
        setAppliedFilters(clearedAppliedFilters);
        setPage(0);
        saveDashboardState({
          filters: clearedAppliedFilters,
          appliedFilters: clearedAppliedFilters,
          page: 0,
          rowsPerPage: rowsPerPageRef.current,
          scrollY: window.scrollY,
        });
      }
    } finally {
      setLoading(false);
    }
  }, [appliedFilters, puedeVerDashboard, rolActual]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      if (document.visibilityState === "visible") {
        loadData();
      }
    }, 180000);

    return () => clearInterval(intervalId);
  }, [loadData]);

  useEffect(() => {
    if (loading || restoredScrollRef.current) return undefined;

    restoredScrollRef.current = true;
    const scrollY = Number(storedDashboardState?.scrollY) || 0;

    if (scrollY <= 0) return undefined;

    const frameId = window.requestAnimationFrame(() => {
      window.scrollTo({ top: scrollY, behavior: "auto" });
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [loading, storedDashboardState]);

  const backendFilters = dashboardResponse.filters || {};
  const requiresProjectFilter = dashboardResponse.meta?.requiresProjectFilter === true;

  const projectOptions = Array.isArray(backendFilters.proyectos) ? backendFilters.proyectos : [];
  const convocatoriaOptions = Array.isArray(backendFilters.convocatorias) ? backendFilters.convocatorias : [];
  const estadoOperativoOptions =
    Array.isArray(backendFilters.estadosOperativos) && backendFilters.estadosOperativos.length
      ? backendFilters.estadosOperativos
      : canonicalOperationalStateOptions;
  const comunidadOptions = Array.isArray(backendFilters.comunidades) ? backendFilters.comunidades : [];
  const actividadOptions = Array.isArray(backendFilters.actividades) ? backendFilters.actividades : [];

  const handleFilterChange = (key, value) => {
    setFilters((current) => ({ ...current, [key]: value }));
  };

  const handleProjectChange = (event) => {
    const selectedValue = event.target.value;
    const selectedProject = projectOptions.find((option) => String(getProjectId(option)) === String(selectedValue));

    setFilters((current) => ({
      ...current,
      idProyecto: selectedValue,
      codigoProyecto: selectedProject ? getProjectCode(selectedProject) : "",
      idConvocatoria: "",
      todosProyectos: false,
    }));
  };

  const handleTodosProyectosChange = (event) => {
    const checked = event.target.checked;

    setFilters((current) => ({
      ...current,
      todosProyectos: checked,
      idProyecto: checked ? "" : current.idProyecto,
      codigoProyecto: checked ? "" : current.codigoProyecto,
      idConvocatoria: checked ? "" : current.idConvocatoria,
    }));
  };

  const handleApplyFilters = () => {
    const nextAppliedFilters = { ...filters };

    setAppliedFilters(nextAppliedFilters);
    setPage(0);
    saveDashboardState({
      filters,
      appliedFilters: nextAppliedFilters,
      page: 0,
      rowsPerPage,
      scrollY: window.scrollY,
    });
  };

  const handleClearFilters = () => {
    const clearedFilters = { ...initialFilters };

    setFilters(clearedFilters);
    setAppliedFilters(clearedFilters);
    setPage(0);
    saveDashboardState({
      filters: clearedFilters,
      appliedFilters: clearedFilters,
      page: 0,
      rowsPerPage,
      scrollY: window.scrollY,
    });
  };

  const kpis = dashboardResponse.kpis || {};
  const charts = dashboardResponse.charts || {};

  const jornadasPorDia = useMemo(() => {
    const source = Array.isArray(charts.jornadasPorDia) ? charts.jornadasPorDia : [];

      return [
        {
          id: MEDICAL_DEPLOYMENT_LABELS.pluralShort,
        color: "#009fe3",
        data: [...source]
          .filter((item) => item.fecha)
          .sort((a, b) => sortDateAsc(a.fecha, b.fecha))
          .map((item) => ({
            x: formatDate(item.fecha),
            y: asNumber(item.total),
          })),
      },
    ];
  }, [charts.jornadasPorDia]);

  const estadoConvocatorias = useMemo(
    () =>
      (Array.isArray(charts.estadoConvocatorias) ? charts.estadoConvocatorias : [])
        .map((item) => ({
          id: item.id || item.label || item.estado || "Sin dato",
          label: item.label || item.id || item.estado || "Sin dato",
          value: asNumber(item.value ?? item.total),
        }))
        .filter((item) => item.id && item.value > 0),
    [charts.estadoConvocatorias]
  );

  const voluntariosPorEstado = useMemo(
    () =>
      (Array.isArray(charts.voluntariosPorEstado) ? charts.voluntariosPorEstado : [])
        .map((item) => ({
          id: item.id || item.label || item.estado || "Sin dato",
          label: item.label || item.id || item.estado || "Sin dato",
          value: asNumber(item.value ?? item.total),
        }))
        .filter((item) => item.id && item.value > 0),
    [charts.voluntariosPorEstado]
  );

  const atencionesPorJornada = useMemo(
    () =>
      limitChartRanking(
        (Array.isArray(charts.atencionesPorJornada) ? charts.atencionesPorJornada : []).map((item) => ({
          jornada: item.jornada || item.nombreJornada || item.descripcionConvo || item.label || item.id || "Sin dato",
          total: asNumber(item.total ?? item.value ?? item.atenciones),
        })),
        10,
        true
      ),
    [charts.atencionesPorJornada]
  );

  const jornadasPorComunidad = useMemo(
    () =>
      limitChartRanking(
        (Array.isArray(charts.jornadasPorComunidad) ? charts.jornadasPorComunidad : []).map((item) => ({
          comunidad: item.comunidad || item.label || item.id || "Sin dato",
          total: asNumber(item.total ?? item.value ?? item.jornadas),
        })),
        10,
        true
      ),
    [charts.jornadasPorComunidad]
  );

  const operativeMapPoints = useMemo(() => {
    const rowsByConvocatoria = new Map(
      tableRows.map((row) => [String(row.idConvocatoria ?? row.id_convocatoria ?? ""), row])
    );

    return mapPoints.map((point) => {
      const row = rowsByConvocatoria.get(String(point.idConvocatoria ?? point.id_convocatoria ?? ""));
      const convocados = Number(point.convocados ?? row?.convocados ?? 0);
      const aceptados = Number(point.aceptados ?? row?.aceptados ?? 0);
      const enEspera = Number(point.enEspera ?? row?.enEspera ?? 0);
      const asistenciasRegistradas = Number(
        point.asistenciasRegistradas ?? row?.asistenciasRegistradas ?? row?.asistieron ?? 0
      );
      const atencionesSalud = Number(point.atencionesSalud ?? row?.atencionesSalud ?? 0);

      return {
        ...(row || {}),
        ...point,
        descripcionConvo: point.descripcionConvo ?? row?.descripcionConvo,
        fecha: point.fecha ?? row?.fecha,
        nombreProyecto: point.nombreProyecto ?? row?.nombreProyecto,
        codigoProyecto: point.codigoProyecto ?? row?.codigoProyecto,
        estado: point.estado ?? row?.estado,
        municipio: point.municipio ?? row?.municipio,
        parroquia: point.parroquia ?? row?.parroquia,
        comunidad: point.comunidad ?? row?.comunidad,
        estadoOperativo: point.estadoOperativo ?? row?.estadoOperativo,
        convocados,
        aceptados,
        enEspera,
        asistenciasRegistradas,
        atencionesSalud,
        total: aceptados,
        value: aceptados,
        cantidad: aceptados,
        count: aceptados,
        markerValue: aceptados,
        markerLabel: String(aceptados),
        markerMetricLabel: "Aceptados",
      };
    });
  }, [mapPoints, tableRows]);

  const totalTableRows = tableRows.length;
  const fromTableRow = totalTableRows === 0 ? 0 : page * rowsPerPage + 1;
  const toTableRow = Math.min((page + 1) * rowsPerPage, totalTableRows);
  const paginatedRows = useMemo(
    () => tableRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [page, rowsPerPage, tableRows]
  );

  useEffect(() => {
    if (loading) return;

    const maxPage = Math.max(0, Math.ceil(tableRows.length / rowsPerPage) - 1);

    if (page > maxPage) {
      setPage(maxPage);
    }
  }, [loading, page, rowsPerPage, tableRows.length]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    saveDashboardState({
      filters,
      appliedFilters,
      page: newPage,
      rowsPerPage,
      scrollY: window.scrollY,
    });
  };

  const handleChangeRowsPerPage = (event) => {
    const nextRowsPerPage = parseInt(event.target.value, 10);

    setRowsPerPage(nextRowsPerPage);
    setPage(0);
    saveDashboardState({
      filters,
      appliedFilters,
      page: 0,
      rowsPerPage: nextRowsPerPage,
      scrollY: window.scrollY,
    });
  };

  const handleOpenDetail = (row) => {
    setSelectedRow(row);
    setDetailOpen(true);
  };

  const handleCloseDetail = () => {
    setDetailOpen(false);
    setSelectedRow(null);
  };

  const handleOpenParticipantModal = (row) => {
    const idConvocatoria = row.idConvocatoria ?? row.id_convocatoria ?? row.id;
    const idProyecto = row.idProyecto ?? row.id_proyecto ?? row.idproyectos;

    setParticipantConvocatoria({
      ...row,
      id: idConvocatoria,
      idConvocatoria,
      idProyecto,
    });
    setParticipantModalOpen(true);
  };

  const handleCloseParticipantModal = () => {
    setParticipantModalOpen(false);
    setParticipantConvocatoria(null);
  };

  const handleOpenVolunteersDialog = (row) => {
    const idConvocatoria = row.idConvocatoria ?? row.id_convocatoria ?? row.id;

    setVolunteersConvocatoria({
      ...row,
      id: idConvocatoria,
      idConvocatoria,
    });
    setVolunteersDialogOpen(true);
  };

  const handleCloseVolunteersDialog = () => {
    setVolunteersDialogOpen(false);
    setVolunteersConvocatoria(null);
  };

  const handleOpenStateDialog = (row) => {
    setStateConvocatoria(toConvocatoriaContext(row));
    setStateDialogOpen(true);
  };

  const handleCloseStateDialog = () => {
    setStateDialogOpen(false);
    setStateConvocatoria(null);
  };

  const handleOpenHistoryDialog = (row) => {
    setHistoryConvocatoria(toConvocatoriaContext(row));
    setHistoryDialogOpen(true);
  };

  const handleCloseHistoryDialog = () => {
    setHistoryDialogOpen(false);
    setHistoryConvocatoria(null);
  };

  const handleOpenStateFromDetail = () => {
    if (!selectedRow) return;
    const row = selectedRow;
    handleCloseDetail();
    handleOpenStateDialog(row);
  };

  const handleOpenHistoryFromDetail = () => {
    if (!selectedRow) return;
    const row = selectedRow;
    handleCloseDetail();
    handleOpenHistoryDialog(row);
  };

  const handleOpenAsistencia = (row) => {
    const idConvocatoria = row.idConvocatoria ?? row.id_convocatoria ?? row.id;
    const idProyecto = row.idProyecto ?? row.id_proyecto ?? row.idproyectos;
    const convocatoria = {
      ...row,
      id: idConvocatoria,
      idConvocatoria,
      idProyecto,
    };

    saveDashboardState({
      filters,
      appliedFilters,
      page,
      rowsPerPage,
      scrollY: window.scrollY,
    });

    navigate(
      `/asistencia?con=${encodeURIComponent(JSON.stringify(convocatoria))}&origen=dashboard-operativo`
    );
  };

  const exportReport = () => {
    const headers = [
      "Fecha",
      "Proyecto",
      "Convocatoria",
      "Actividad",
      "Comunidad",
      "Estado operativo",
      "Convocados",
      "Aceptados",
      "En espera",
      "Rechazados",
      "Cancelados",
      "Asistencias",
      "Atenciones salud",
    ];
    const rows = tableRows.map((row) =>
      [
        formatDate(row.fecha),
        row.nombreProyecto || row.codigoProyecto || row.codigo_proyecto,
        row.descripcionConvo,
        formatActivity(row.actividad),
        row.comunidad,
        row.estadoOperativo,
        asNumber(row.convocados),
        asNumber(row.aceptados),
        asNumber(row.enEspera),
        asNumber(row.rechazados),
        asNumber(row.cancelados),
        asNumber(row.asistenciasRegistradas ?? row.asistieron),
        asNumber(row.atencionesSalud),
      ]
        .map((value) => `"${String(value ?? "").replace(/"/g, '""')}"`)
        .join(",")
    );
    const csv = `\uFEFF${[headers.join(","), ...rows].join("\n")}`;
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "dashboard-operativo.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  const nivoTheme = {
    axis: {
      ticks: { text: { fill: theme.palette.secondary[200] } },
      legend: { text: { fill: theme.palette.secondary[200] } },
    },
    legends: { text: { fill: theme.palette.secondary[200] } },
    labels: { text: { fill: theme.palette.secondary[100] } },
    tooltip: { container: { color: theme.palette.primary.main } },
  };
  const selectedEstadoVisual = getEstadoOperativoVisual(selectedRow?.estadoOperativo);
  const selectedEstadoGuardadoVisual = getEstadoOperativoVisual(
    selectedRow?.estadoOperativoGuardado ?? selectedRow?.estado_operativo_guardado
  );
  const selectedStateIsTerminal = isTerminalOperationalStatus(selectedRow?.estadoOperativo);
  const selectedRequiresAttention = isTruthyFlag(
    selectedRow?.requiereAtencionOperativa ?? selectedRow?.requiere_atencion_operativa
  );

  if (!puedeVerDashboard) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="warning">
          <Typography variant="h5" sx={{ fontWeight: 900 }}>
            No tiene permiso para acceder al Dashboard Operativo.
          </Typography>
          <Typography variant="body2">Esta vista esta disponible para roles 1, 2, 6 y 8.</Typography>
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Grid container spacing={2.5}>
        <Grid item xs={12} lg={8}>
          <Stack spacing={1.5}>
            <Chip
              label={`Operativo · proyectos y ${MEDICAL_DEPLOYMENT_LABELS.pluralShort.toLowerCase()}`}
              size="small"
              sx={{
                alignSelf: "flex-start",
                bgcolor: "rgba(0, 159, 227, 0.12)",
                color: "#004f7c",
                fontWeight: 900,
              }}
            />
            <Typography variant={isSmallScreen ? "h3" : "h2"} sx={{ fontWeight: 900, lineHeight: 1.05 }}>
              Dashboard Operativo
            </Typography>
            <Typography variant="h6" color="text.secondary">
              Seguimiento operativo de despliegues de unidades médicas móviles, convocatorias, voluntarios y
              atenciones
            </Typography>
          </Stack>
        </Grid>

        <Grid item xs={12} lg={4}>
          <DashboardCard>
            <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} mb={1}>
              <Typography variant="h5" sx={{ fontWeight: 900, display: "flex", alignItems: "center", gap: 1 }}>
                <Box
                  component="span"
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    bgcolor: "#22a06b",
                    boxShadow: "0 0 0 5px rgba(34,160,107,.14)",
                  }}
                />
                Datos en vivo
              </Typography>
              <Chip label="Auto · cada 3 min" size="small" sx={{ bgcolor: "rgba(0,128,128,.12)", fontWeight: 900 }} />
            </Box>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Ultima actualizacion: <strong>{lastUpdated ? formatDateTime(lastUpdated) : "Sin actualizar"}</strong>
            </Typography>
            <Stack direction={isMediumScreen ? "column" : "row"} spacing={1}>
              <Button
                variant="contained"
                startIcon={<RefreshOutlinedIcon />}
                onClick={loadData}
                disabled={loading}
                sx={{ bgcolor: "teal", "&:hover": { bgcolor: "#006c6c" } }}
              >
                Actualizar ahora
              </Button>
              <Button variant="outlined" startIcon={<CloudDownloadOutlinedIcon />} onClick={exportReport}>
                Exportar CSV
              </Button>
            </Stack>
          </DashboardCard>
        </Grid>

        {error && (
          <Grid item xs={12}>
            <Alert severity="warning">{error}</Alert>
          </Grid>
        )}

        <Grid item xs={12}>
          <DashboardCard>
            <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} mb={2}>
              <Typography variant="h4" sx={{ fontWeight: 900, display: "flex", alignItems: "center" }}>
                <TuneOutlinedIcon sx={{ mr: 1 }} />
                Filtros operativos
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: { xs: "none", md: "block" } }}>
                Seleccione proyecto, despliegue o rango de fechas
              </Typography>
            </Box>

            <Grid container spacing={1.5}>
              {canUseTodosProyectos && (
                <Grid item xs={12} md={2.4}>
                  <FilterCard>
                    <FormControlLabel
                      control={
                        <Checkbox checked={filters.todosProyectos} onChange={handleTodosProyectosChange} />
                      }
                      label="Todos los proyectos"
                    />
                    <Typography variant="caption" color="text.secondary">
                      Recomendado usar rango de fecha.
                    </Typography>
                  </FilterCard>
                </Grid>
              )}
              <Grid item xs={12} sm={6} md={2.4}>
                <FilterCard>
                  <TextField
                    select
                    label="Proyecto"
                    value={filters.idProyecto}
                    onChange={handleProjectChange}
                    fullWidth
                    size="small"
                    disabled={filters.todosProyectos}
                  >
                    <MenuItem value="">Seleccione</MenuItem>
                    {projectOptions.map((option) => {
                      const value = String(getProjectId(option));
                      const label = getProjectName(option);
                      const visual = getProjectVisual(option);

                      return (
                        <MenuItem key={value || label} value={value}>
                          {visual ? `${label} · ${visual}` : label}
                        </MenuItem>
                      );
                    })}
                  </TextField>
                </FilterCard>
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <FilterCard>
                  <TextField
                    label="Fecha desde"
                    type="date"
                    value={filters.fechaDesde}
                    onChange={(event) => handleFilterChange("fechaDesde", event.target.value)}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    size="small"
                  />
                </FilterCard>
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <FilterCard>
                  <TextField
                    label="Fecha hasta"
                    type="date"
                    value={filters.fechaHasta}
                    onChange={(event) => handleFilterChange("fechaHasta", event.target.value)}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    size="small"
                  />
                </FilterCard>
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <FilterCard>
                  <TextField
                    select
                    label="Convocatoria / Despliegue"
                    value={filters.idConvocatoria}
                    onChange={(event) => handleFilterChange("idConvocatoria", event.target.value)}
                    fullWidth
                    size="small"
                  >
                    <MenuItem value="">Todas</MenuItem>
                    {convocatoriaOptions.map((option) => {
                      const value = String(getConvocatoriaId(option));

                      return (
                        <MenuItem key={value || getConvocatoriaLabel(option)} value={value}>
                          {getConvocatoriaLabel(option)}
                        </MenuItem>
                      );
                    })}
                  </TextField>
                </FilterCard>
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <FilterCard>
                  <TextField
                    select
                    label="Estado operativo"
                    value={filters.estadoOperativo}
                    onChange={(event) => handleFilterChange("estadoOperativo", event.target.value)}
                    fullWidth
                    size="small"
                  >
                    <MenuItem value="">Todos</MenuItem>
                    {estadoOperativoOptions.map((option) => {
                      const value = getOptionValue(option, ["estadoOperativo", "estado_operativo", "id"]);
                      const label = getOptionLabel(option, ["label", "estadoOperativo", "estado_operativo", "nombre"]);
                      const estadoVisual = getEstadoOperativoVisual(label || value);

                      return (
                        <MenuItem key={value || label} value={value}>
                          <Chip
                            label={estadoVisual.label}
                            color={estadoVisual.chipColor}
                            size="small"
                            variant="filled"
                            sx={{ minWidth: 100, fontWeight: 900 }}
                          />
                        </MenuItem>
                      );
                    })}
                  </TextField>
                </FilterCard>
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <FilterCard>
                  <TextField
                    select
                    label="Comunidad"
                    value={filters.comunidad}
                    onChange={(event) => handleFilterChange("comunidad", event.target.value)}
                    fullWidth
                    size="small"
                  >
                    <MenuItem value="">Todas</MenuItem>
                    {comunidadOptions.map((option) => {
                      const value = getOptionValue(option, ["comunidad", "nombre"]);
                      const label = getOptionLabel(option, ["label", "comunidad", "nombre"]);

                      return (
                        <MenuItem key={value || label} value={value}>
                          {label}
                        </MenuItem>
                      );
                    })}
                  </TextField>
                </FilterCard>
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <FilterCard>
                  <TextField
                    select
                    label="Actividad"
                    value={filters.actividad}
                    onChange={(event) => handleFilterChange("actividad", event.target.value)}
                    fullWidth
                    size="small"
                  >
                    <MenuItem value="">Todas</MenuItem>
                    {actividadOptions.map((option) => {
                      const value = getOptionValue(option, ["actividad", "tipoActividad", "tipo_actividad", "nombre"]);
                      const label = formatActivity(
                        getOptionLabel(option, ["label", "actividad", "tipoActividad", "tipo_actividad", "nombre"])
                      );

                      return (
                        <MenuItem key={value || label} value={value}>
                          {label}
                        </MenuItem>
                      );
                    })}
                  </TextField>
                </FilterCard>
              </Grid>
            </Grid>

            <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: "wrap", gap: 1 }}>
              <Button variant="contained" onClick={handleApplyFilters} sx={{ bgcolor: "teal" }}>
                Aplicar filtros
              </Button>
              <Button variant="outlined" onClick={handleClearFilters}>
                Limpiar filtros
              </Button>
            </Stack>
          </DashboardCard>
        </Grid>

        {loading ? (
          <Grid item xs={12}>
            <DashboardCard>
              <Box display="flex" justifyContent="center" alignItems="center" minHeight={220}>
                <CircularProgress />
              </Box>
            </DashboardCard>
          </Grid>
        ) : requiresProjectFilter ? (
          <Grid item xs={12}>
            <DashboardCard>
              <Alert severity="info" sx={{ alignItems: "center" }}>
                <Typography variant="h6" sx={{ fontWeight: 900 }}>
                  {dashboardResponse.meta?.message ||
                    "Seleccione un proyecto o active Todos los proyectos para consultar el dashboard operativo."}
                </Typography>
                <Typography variant="body2">
                  La primera carga solo trae filtros livianos para evitar consultar toda la tabla operativa.
                </Typography>
              </Alert>
            </DashboardCard>
          </Grid>
        ) : (
          <>
            <Grid item xs={12} sm={6} lg={2.4}>
              <StatCard title={`${MEDICAL_DEPLOYMENT_LABELS.pluralShort} totales`} value={kpis.jornadasTotal} caption="Convocatorias" color="#009fe3" icon={<CalendarMonthOutlinedIcon sx={{ color: "white" }} />} />
            </Grid>
            <Grid item xs={12} sm={6} lg={2.4}>
              <StatCard title={`${MEDICAL_DEPLOYMENT_LABELS.pluralShort} activos`} value={kpis.jornadasActivas} caption="En curso" color="#22a06b" icon={<TaskAltOutlinedIcon sx={{ color: "white" }} />} />
            </Grid>
            <Grid item xs={12} sm={6} lg={2.4}>
              <StatCard title={`${MEDICAL_DEPLOYMENT_LABELS.pluralShort} de hoy`} value={kpis.jornadasHoy} caption="Operativo diario" color="#004f7c" icon={<AssignmentTurnedInOutlinedIcon sx={{ color: "white" }} />} />
            </Grid>
            <Grid item xs={12} sm={6} lg={2.4}>
              <StatCard title={`${MEDICAL_DEPLOYMENT_LABELS.pluralShort} finalizados`} value={kpis.jornadasFinalizadas} caption="Cerrados" color="#008080" icon={<DoneAllOutlinedIcon sx={{ color: "white" }} />} />
            </Grid>
            <Grid item xs={12} sm={6} lg={2.4}>
              <StatCard title="Convocados" value={kpis.voluntariosConvocados} caption="Voluntarios" color="#f39313" icon={<GroupsOutlinedIcon sx={{ color: "white" }} />} />
            </Grid>
            <Grid item xs={12} sm={6} lg={2.4}>
              <StatCard title="Aceptados" value={kpis.voluntariosAceptados} caption="Confirmados" color="#22a06b" icon={<GroupsOutlinedIcon sx={{ color: "white" }} />} />
            </Grid>
            <Grid item xs={12} sm={6} lg={2.4}>
              <StatCard title="En espera" value={kpis.voluntariosEnEspera} caption="Pendientes" color="#f39313" icon={<PendingActionsOutlinedIcon sx={{ color: "white" }} />} />
            </Grid>
            <Grid item xs={12} sm={6} lg={2.4}>
              <StatCard title="Asistencias registradas" value={kpis.asistenciasRegistradas} caption="Registros procesados" color="#d94841" icon={<AssignmentTurnedInOutlinedIcon sx={{ color: "white" }} />} />
            </Grid>
            <Grid item xs={12} sm={6} lg={2.4}>
              <StatCard title="Atenciones salud" value={kpis.atencionesSalud} caption="Registros salud" color="#009fe3" icon={<LocalHospitalOutlinedIcon sx={{ color: "white" }} />} />
            </Grid>
            <Grid item xs={12} sm={6} lg={2.4}>
              <StatCard title="Comunidades" value={kpis.comunidadesAtendidas} caption="Cobertura" color="#004f7c" icon={<PlaceOutlinedIcon sx={{ color: "white" }} />} />
            </Grid>

            <Grid item xs={12}>
              <DashboardCard>
                <SectionTitle
                  title="Mapa operativo de despliegues"
                  pill={`${operativeMapPoints.length} puntos con coordenadas`}
                  icon={<PlaceOutlinedIcon sx={{ mr: 1 }} />}
                />
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  El numero del marcador representa voluntarios aceptados.
                </Typography>
                <JornadasMap
                  points={operativeMapPoints}
                  height={420}
                  mode="operativo"
                  onManageOperationalStatus={handleOpenStateDialog}
                  onViewOperationalHistory={handleOpenHistoryDialog}
                  onViewVolunteers={handleOpenVolunteersDialog}
                />
              </DashboardCard>
            </Grid>

            <Grid item xs={12} lg={6}>
              <DashboardCard>
                <SectionTitle title="Despliegues por día" pill="Cronológico" />
                <Box height={300}>
                  {hasLineData(jornadasPorDia) ? (
                    <ResponsiveLine
                      data={jornadasPorDia}
                      theme={nivoTheme}
                      margin={{ top: 20, right: 25, bottom: 55, left: 45 }}
                      xScale={{ type: "point" }}
                      yScale={{ type: "linear", min: 0, max: "auto", stacked: false }}
                      curve="monotoneX"
                      enableArea
                      areaOpacity={0.12}
                      colors={["#009fe3"]}
                      pointSize={8}
                      pointBorderWidth={2}
                      pointBorderColor={{ from: "serieColor" }}
                      useMesh
                      axisTop={null}
                      axisRight={null}
                      axisBottom={{ tickSize: 5, tickPadding: 5, tickRotation: isSmallScreen ? 35 : 0 }}
                      axisLeft={{ tickSize: 5, tickPadding: 5, tickRotation: 0, tickValues: 4 }}
                    />
                  ) : (
                    <EmptyChartState />
                  )}
                </Box>
              </DashboardCard>
            </Grid>

            <Grid item xs={12} lg={6}>
              <DashboardCard>
                <SectionTitle title="Despliegues por comunidad" pill="Top 10" />
                <Box height={300}>
                  {hasBarData(jornadasPorComunidad) ? (
                    <ResponsiveBar
                      data={jornadasPorComunidad}
                      keys={["total"]}
                      indexBy="comunidad"
                      theme={nivoTheme}
                      margin={{ top: 20, right: 24, bottom: 70, left: 52 }}
                      padding={0.35}
                      colors={chartColors}
                      colorBy="indexValue"
                      borderRadius={8}
                      axisTop={null}
                      axisRight={null}
                      axisBottom={{ tickSize: 5, tickPadding: 5, tickRotation: isSmallScreen ? 35 : 20 }}
                      axisLeft={{ tickSize: 5, tickPadding: 5, tickRotation: 0, tickValues: 4 }}
                      enableLabel={false}
                    />
                  ) : (
                    <EmptyChartState />
                  )}
                </Box>
              </DashboardCard>
            </Grid>

            <Grid item xs={12} lg={4}>
              <DashboardCard>
                <SectionTitle title="Estado de convocatorias" pill="Distribucion" />
                <Box height={290}>
                  {hasPieData(estadoConvocatorias) ? (
                    <ResponsivePie
                      data={estadoConvocatorias}
                      theme={nivoTheme}
                      margin={{ top: 20, right: 20, bottom: 65, left: 20 }}
                      innerRadius={0.55}
                      padAngle={1}
                      cornerRadius={4}
                      activeOuterRadiusOffset={8}
                      colors={chartColors}
                      borderWidth={1}
                      borderColor={{ from: "color", modifiers: [["darker", 0.2]] }}
                      arcLabelsSkipAngle={10}
                      arcLinkLabelsSkipAngle={12}
                    />
                  ) : (
                    <EmptyChartState />
                  )}
                </Box>
              </DashboardCard>
            </Grid>

            <Grid item xs={12} lg={4}>
              <DashboardCard>
                <SectionTitle title="Voluntarios por estado" pill="Convocatoria" />
                <Box height={290}>
                  {hasPieData(voluntariosPorEstado) ? (
                    <ResponsivePie
                      data={voluntariosPorEstado}
                      theme={nivoTheme}
                      margin={{ top: 20, right: 20, bottom: 65, left: 20 }}
                      innerRadius={0.55}
                      padAngle={1}
                      cornerRadius={4}
                      activeOuterRadiusOffset={8}
                      colors={["#22a06b", "#f39313", "#d94841", "#004f7c"]}
                      borderWidth={1}
                      borderColor={{ from: "color", modifiers: [["darker", 0.2]] }}
                      arcLabelsSkipAngle={10}
                      arcLinkLabelsSkipAngle={12}
                    />
                  ) : (
                    <EmptyChartState />
                  )}
                </Box>
              </DashboardCard>
            </Grid>

            <Grid item xs={12} lg={4}>
              <DashboardCard>
                <SectionTitle title="Atenciones por despliegue" pill="Top 10" />
                <Box height={290}>
                  {hasBarData(atencionesPorJornada) ? (
                    <ResponsiveBar
                      data={atencionesPorJornada}
                      keys={["total"]}
                      indexBy="jornada"
                      theme={nivoTheme}
                      margin={{ top: 20, right: 24, bottom: 80, left: 52 }}
                      padding={0.35}
                      colors={chartColors}
                      colorBy="indexValue"
                      borderRadius={8}
                      axisTop={null}
                      axisRight={null}
                      axisBottom={{ tickSize: 5, tickPadding: 5, tickRotation: 35 }}
                      axisLeft={{ tickSize: 5, tickPadding: 5, tickRotation: 0, tickValues: 4 }}
                      enableLabel={false}
                    />
                  ) : (
                    <EmptyChartState />
                  )}
                </Box>
              </DashboardCard>
            </Grid>

            <Grid item xs={12}>
              <DashboardCard>
                <SectionTitle title="Detalle operativo de despliegues" pill="Tabla exportable" />
                <Divider sx={{ mb: 2 }} />
                <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} sx={{ mb: 1.5, flexWrap: "wrap" }}>
                  <Typography variant="h5" sx={{ fontWeight: 800 }}>
                    {MEDICAL_DEPLOYMENT_LABELS.plural}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Mostrando {fromTableRow}-{toTableRow} de {totalTableRows} registros
                  </Typography>
                </Box>
                <TableContainer sx={{ width: "100%", maxHeight: 560, overflow: "auto" }}>
                  <Table stickyHeader size="small" aria-label="Detalle operativo de despliegues">
                    <TableHead>
                      <TableRow>
                        {[
                          "Fecha",
                          "Proyecto",
                          "Convocatoria / Despliegue",
                          "Actividad",
                          "Comunidad",
                          "Estado operativo",
                          "Convocados",
                          "Aceptados",
                          "En espera",
                          "Rechazados",
                          "Cancelados",
                          "Asistencias",
                          "Atenciones salud",
                          "Acciones",
                        ].map((column) => (
                          <TableCell
                            key={column}
                            sx={{
                              fontWeight: 900,
                              backgroundColor: "#004f7c",
                              color: "white",
                              whiteSpace: "nowrap",
                              textTransform: "uppercase",
                              letterSpacing: ".04em",
                              fontSize: 11,
                            }}
                          >
                            {column}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedRows.map((row, index) => {
                        const estadoVisual = getEstadoOperativoVisual(row.estadoOperativo);
                        const terminalState = isTerminalOperationalStatus(row.estadoOperativo);
                        const participantAdditionAllowed = canAddParticipants(row);

                        return (
                          <TableRow
                            key={`${row.idConvocatoria || row.idProyecto || "row"}-${index}`}
                            hover
                            sx={{
                              borderLeft: `5px solid ${estadoVisual.border}`,
                              backgroundColor:
                                theme.palette.mode === "dark" ? "transparent" : estadoVisual.background,
                              "&:hover": {
                                backgroundColor:
                                  theme.palette.mode === "dark"
                                    ? "rgba(255,255,255,0.06)"
                                    : estadoVisual.background,
                              },
                            }}
                          >
                          <TableCell sx={{ whiteSpace: "nowrap", fontWeight: 700 }}>{formatDate(row.fecha)}</TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap", fontWeight: 700 }}>{textOrDash(row.nombreProyecto || row.codigoProyecto)}</TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap", fontWeight: 700 }}>{textOrDash(row.descripcionConvo)}</TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap", fontWeight: 700 }}>{formatActivity(row.actividad)}</TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap", fontWeight: 700 }}>{textOrDash(row.comunidad)}</TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                            <Chip
                              label={estadoVisual.label}
                              color={estadoVisual.chipColor}
                              size="small"
                              variant="filled"
                              sx={{ fontWeight: 900, minWidth: 100 }}
                            />
                          </TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap", fontWeight: 700 }}>{asNumber(row.convocados)}</TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap", fontWeight: 700 }}>{asNumber(row.aceptados)}</TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap", fontWeight: 700 }}>{asNumber(row.enEspera)}</TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap", fontWeight: 700 }}>{asNumber(row.rechazados)}</TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap", fontWeight: 700 }}>{asNumber(row.cancelados)}</TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap", fontWeight: 700 }}>{asNumber(row.asistenciasRegistradas ?? row.asistieron)}</TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap", fontWeight: 700 }}>{asNumber(row.atencionesSalud)}</TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                            <Stack direction="row" spacing={0.5}>
                              <Tooltip title="Ver detalle">
                                <Button
                                  size="small"
                                  color="info"
                                  variant="outlined"
                                  aria-label="Ver detalle"
                                  onClick={() => handleOpenDetail(row)}
                                  sx={{ minWidth: 36, px: 0.75 }}
                                >
                                  <VisibilityOutlinedIcon fontSize="small" />
                                </Button>
                              </Tooltip>
                              <Tooltip
                                title={
                                  terminalState
                                    ? "Este despliegue se encuentra en un estado terminal."
                                    : "Gestionar estado operativo"
                                }
                              >
                                <span>
                                  <Button
                                    size="small"
                                    color={estadoVisual.chipColor === "warning" ? "warning" : "primary"}
                                    variant="outlined"
                                    aria-label="Gestionar estado operativo"
                                    onClick={() => handleOpenStateDialog(row)}
                                    disabled={terminalState}
                                    sx={{ minWidth: 36, px: 0.75 }}
                                  >
                                    <PublishedWithChangesOutlinedIcon fontSize="small" />
                                  </Button>
                                </span>
                              </Tooltip>
                              <Tooltip title="Ver historial de estados">
                                <Button
                                  size="small"
                                  color="info"
                                  variant="outlined"
                                  aria-label="Ver historial de estados"
                                  onClick={() => handleOpenHistoryDialog(row)}
                                  sx={{ minWidth: 36, px: 0.75 }}
                                >
                                  <HistoryOutlinedIcon fontSize="small" />
                                </Button>
                              </Tooltip>
                              <Tooltip title="Ver voluntarios">
                                <Button
                                  size="small"
                                  color="secondary"
                                  variant="outlined"
                                  aria-label="Ver voluntarios"
                                  onClick={() => handleOpenVolunteersDialog(row)}
                                  sx={{ minWidth: 36, px: 0.75 }}
                                >
                                  <GroupsOutlinedIcon fontSize="small" />
                                </Button>
                              </Tooltip>
                              <Tooltip title="Control de asistencia">
                                <Button
                                  size="small"
                                  color="success"
                                  variant="outlined"
                                  aria-label="Control de asistencia"
                                  onClick={() => handleOpenAsistencia(row)}
                                  sx={{ minWidth: 36, px: 0.75 }}
                                >
                                  <AssignmentTurnedInOutlinedIcon fontSize="small" />
                                </Button>
                              </Tooltip>
                              <Tooltip
                                title={
                                  participantAdditionAllowed
                                    ? "Agregar participante"
                                    : "Este despliegue no está activo y no admite nuevos participantes."
                                }
                              >
                                <span>
                                  <Button
                                    size="small"
                                    color="warning"
                                    variant="outlined"
                                    aria-label="Agregar participante"
                                    onClick={() => handleOpenParticipantModal(row)}
                                    disabled={!participantAdditionAllowed}
                                    sx={{ minWidth: 36, px: 0.75 }}
                                  >
                                    <PersonAddAltOutlinedIcon fontSize="small" />
                                  </Button>
                                </span>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                          </TableRow>
                        );
                      })}
                      {tableRows.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={14} align="center" sx={{ py: 4 }}>
                            No hay despliegues para los filtros seleccionados.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  count={tableRows.length}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[25, 50, 100]}
                  labelRowsPerPage="Filas por pagina"
                  labelDisplayedRows={({ from, to, count }) =>
                    `${from}-${to} de ${count !== -1 ? count : `mas de ${to}`}`
                  }
                />
              </DashboardCard>
            </Grid>
          </>
        )}
      </Grid>

      <Modal open={detailOpen} onClose={handleCloseDetail}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: isSmallScreen ? "92%" : 720,
            maxHeight: "90vh",
            overflow: "auto",
            bgcolor: theme.palette.background.alt,
            border: "1px solid rgba(15, 23, 42, 0.18)",
            borderRadius: 3,
            boxShadow: 24,
            p: 3,
          }}
        >
          <Typography variant="h4" sx={{ fontWeight: 900, mb: 1 }}>
            Detalle del {MEDICAL_DEPLOYMENT_LABELS.singular}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {textOrDash(selectedRow?.descripcionConvo)}
          </Typography>
          <Divider sx={{ mb: 2 }} />
          {selectedRequiresAttention && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Este despliegue requiere revisión operativa.
            </Alert>
          )}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <DetailRow label="Proyecto" value={selectedRow?.nombreProyecto || selectedRow?.codigoProyecto} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DetailRow label="Socio" value={selectedRow?.socio} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DetailRow label="Fecha" value={formatDate(selectedRow?.fecha)} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DetailRow label="Actividad" value={formatActivity(selectedRow?.actividad)} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
                Estado operativo
              </Typography>
              <Box mt={0.5}>
                <Chip
                  label={selectedEstadoVisual.label}
                  color={selectedEstadoVisual.chipColor}
                  size="small"
                  variant="filled"
                  sx={{ fontWeight: 900, minWidth: 100 }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <DetailRow label="Comunidad" value={selectedRow?.comunidad} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DetailRow
                label="Origen del estado"
                value={getOperationalOriginLabel(
                  selectedRow?.estadoOperativoOrigen ?? selectedRow?.estado_operativo_origen
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DetailRow label="Estado guardado" value={selectedEstadoGuardadoVisual.label} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DetailRow
                label="Actualizado por"
                value={
                  selectedRow?.actualizadoPorNombre ??
                  selectedRow?.actualizado_por_nombre ??
                  selectedRow?.actualizadoPor ??
                  selectedRow?.actualizado_por
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DetailRow
                label="Actualizado en"
                value={formatBackendDateTime(
                  selectedRow?.actualizadoEn ?? selectedRow?.actualizado_en
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <DetailRow
                label="Motivo del estado"
                value={selectedRow?.motivo ?? selectedRow?.motivoEstadoOperativo ?? selectedRow?.motivo_estado_operativo}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DetailRow label="Requiere atención operativa" value={selectedRequiresAttention ? "Sí" : "No"} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DetailRow label="Territorio" value={[selectedRow?.estado, selectedRow?.municipio, selectedRow?.parroquia].filter(Boolean).join(" / ")} />
            </Grid>
            <Grid item xs={12}>
              <DetailRow label="Punto de encuentro" value={selectedRow?.puntoEncuentro} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <DetailRow label="Hora encuentro" value={selectedRow?.horaEncuentro} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <DetailRow label="Hora salida" value={selectedRow?.horaSalida} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <DetailRow label="Hora retorno" value={selectedRow?.horaRetorno} />
            </Grid>
            <Grid item xs={12}>
              <Divider />
            </Grid>
            {[
              ["Convocados", selectedRow?.convocados],
              ["Aceptados", selectedRow?.aceptados],
              ["En espera", selectedRow?.enEspera],
              ["Rechazados", selectedRow?.rechazados],
              ["Cancelados", selectedRow?.cancelados],
              ["Asistencias", selectedRow?.asistenciasRegistradas ?? selectedRow?.asistieron],
              ["Atenciones salud", selectedRow?.atencionesSalud],
            ].map(([label, value]) => (
              <Grid item xs={6} sm={3} key={label}>
                <DetailRow label={label} value={asNumber(value)} />
              </Grid>
            ))}
            <Grid item xs={12}>
              <DetailRow label="Comentarios finales" value={selectedRow?.comentariosFinales} />
            </Grid>
          </Grid>
          <Divider sx={{ my: 2 }} />
          <Stack direction={{ xs: "column", sm: "row" }} justifyContent="flex-end" spacing={1}>
            <Tooltip
              title={
                selectedStateIsTerminal
                  ? "Este despliegue se encuentra en un estado terminal."
                  : "Gestionar estado operativo"
              }
            >
              <span>
                <Button
                  variant="outlined"
                  startIcon={<PublishedWithChangesOutlinedIcon />}
                  onClick={handleOpenStateFromDetail}
                  disabled={selectedStateIsTerminal}
                  fullWidth={isSmallScreen}
                >
                  Gestionar estado
                </Button>
              </span>
            </Tooltip>
            <Button
              variant="outlined"
              color="info"
              startIcon={<HistoryOutlinedIcon />}
              onClick={handleOpenHistoryFromDetail}
            >
              Ver historial
            </Button>
            <Button variant="contained" color="error" onClick={handleCloseDetail}>
              Cerrar
            </Button>
          </Stack>
        </Box>
      </Modal>

      <AgregarParticipantesConvocatoriaModal
        open={participantModalOpen}
        onClose={handleCloseParticipantModal}
        convocatoria={participantConvocatoria}
        onSuccess={async () => {
          await loadData();
        }}
        allowedRoles={allowedRoles}
      />

      <VoluntariosConvocatoriaOperativaDialog
        open={volunteersDialogOpen}
        onClose={handleCloseVolunteersDialog}
        convocatoria={volunteersConvocatoria}
        allowedRoles={allowedRoles}
      />

      <EstadoOperativoDialog
        open={stateDialogOpen}
        onClose={handleCloseStateDialog}
        convocatoria={stateConvocatoria}
        onUpdated={loadData}
        allowedRoles={allowedRoles}
      />

      <HistorialEstadoOperativoDialog
        open={historyDialogOpen}
        onClose={handleCloseHistoryDialog}
        convocatoria={historyConvocatoria}
        allowedRoles={allowedRoles}
      />
    </Container>
  );
};

export default DashboardOperativo;
